/**
 * Admin · Sales Signals — tiny CRM aggregating buying-stage signals
 * One feed: DD-pack downloads + lead-magnet captures, with "Mark contacted".
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Activity, CheckCircle2, RefreshCcw, Filter, Mail, Building2,
  Flame, Users, Target, Inbox, Send, Copy,
} from 'lucide-react';
import { toast } from 'sonner';
import BrandTitle from '../components/BrandTitle';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const intentBadge = (intent) => {
  const map = {
    high: { cls: 'bg-rose-500/15 text-rose-700 border-rose-300 dark:bg-rose-500/15 dark:text-rose-300 dark:border-rose-500/30', label: '🔥 High intent' },
    medium: { cls: 'bg-amber-500/15 text-amber-700 border-amber-300 dark:text-amber-300 dark:border-amber-500/30', label: 'Medium' },
    context: { cls: 'bg-slate-500/10 text-slate-400 border-slate-300 dark:text-slate-300 dark:border-slate-500/30', label: 'Context' },
  };
  const m = map[intent] || map.context;
  return <Badge className={m.cls} data-testid={`intent-${intent}`}>{m.label}</Badge>;
};

const fmtRelTime = (iso) => {
  if (!iso) return '—';
  const ts = new Date(iso).getTime();
  const diff = (Date.now() - ts) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const AdminSalesSignals = () => {
  const [loading, setLoading] = useState(true);
  const [signals, setSignals] = useState([]);
  const [stats, setStats] = useState(null);
  const [lanes, setLanes] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [activeLane, setActiveLane] = useState('all');
  const [activeTier, setActiveTier] = useState('all');
  const [onlyUncontacted, setOnlyUncontacted] = useState(true);
  const [pendingId, setPendingId] = useState(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = async () => {
    setLoading(true);
    try {
      const params = { only_uncontacted: onlyUncontacted, limit: 200 };
      if (activeLane !== 'all') params.lane = activeLane;
      if (activeTier !== 'all') params.tier = activeTier;
      const [sigRes, stRes, lnRes, trRes] = await Promise.all([
        axios.get(`${API_URL}/admin/sales-signals`, { ...auth(), params }),
        axios.get(`${API_URL}/admin/sales-signals/stats`, auth()),
        axios.get(`${API_URL}/admin/sales-signals/lanes`, auth()),
        axios.get(`${API_URL}/admin/sales-signals/tiers`, auth()),
      ]);
      setSignals(sigRes.data.signals || []);
      setStats(stRes.data);
      setLanes(lnRes.data.lanes || []);
      setTiers(trRes.data.tiers || []);
    } catch (e) {
      toast.error('Could not load sales signals', { description: e?.response?.data?.detail });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [onlyUncontacted, activeLane, activeTier]);

  const toggleContacted = async (signal) => {
    setPendingId(signal.signal_id);
    const verb = signal.contacted ? 'uncontacted' : 'contacted';
    try {
      await axios.post(
        `${API_URL}/admin/sales-signals/${encodeURIComponent(signal.signal_id)}/${verb}`,
        {}, auth(),
      );
      toast.success(signal.contacted ? 'Re-opened' : 'Marked contacted', {
        description: signal.company || signal.email,
      });
      await load();
    } catch (e) {
      toast.error('Could not update');
    } finally { setPendingId(null); }
  };

  const copyReplyTemplate = (signal) => {
    if (!signal.reply_template) return;
    navigator.clipboard.writeText(signal.reply_template);
    toast.success(`${signal.lane_label} template copied`, {
      description: 'Paste into your email client and personalise the rest.',
    });
  };

  const grouped = useMemo(() => {
    const today = [], earlier = [];
    const todayCutoff = Date.now() - 86400_000;
    for (const s of signals) {
      const ts = new Date(s.happened_at || 0).getTime();
      if (ts >= todayCutoff) today.push(s); else earlier.push(s);
    }
    return { today, earlier };
  }, [signals]);

  const laneAccent = (key) => ({
    cfo: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
    engineer: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-cyan-300',
    infosec: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-blue-300',
    other: 'border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-300',
  }[key] || 'border-border');

  const SignalRow = ({ s }) => (
    <Card
      className={`transition-all ${s.contacted ? 'opacity-50 border-slate-200' : s.intent === 'high' ? 'border-rose-500/30 bg-gradient-to-r from-rose-500/5 to-transparent' : 'border-border'}`}
      data-testid={`signal-row-${s.signal_id}`}
    >
      <CardContent className="p-4 flex items-start gap-4">
        <div className="shrink-0 mt-1">
          {s.kind === 'dd_pack_download' && <Target className="h-5 w-5 text-rose-500" />}
          {s.kind === 'lead_magnet' && <Inbox className="h-5 w-5 text-amber-500" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <h3 className="font-semibold truncate">{s.company || s.domain || s.email || '—'}</h3>
            {s.lane && (
              <Badge variant="outline" className={`text-[11px] ${laneAccent(s.lane)}`} data-testid={`lane-badge-${s.lane}`}>
                {s.lane_emoji} {s.lane_label}
              </Badge>
            )}
            {s.spend_tier && s.spend_tier !== 'unknown' && (
              <Badge variant="outline" className="text-[11px] border-slate-500/40 bg-slate-500/10 text-slate-700 dark:text-slate-200" data-testid={`tier-badge-${s.spend_tier}`}>
                {s.spend_tier_emoji} {s.spend_tier_label}{s.monthly_cloud_spend_usd ? ` · $${Math.round(s.monthly_cloud_spend_usd / 1000)}K/mo` : ''}
              </Badge>
            )}
            {intentBadge(s.intent)}
            <Badge variant="outline" className="text-[11px]">{s.kind_label}</Badge>
            {s.contacted && (
              <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30 text-[11px]">
                <CheckCircle2 className="h-3 w-3 mr-1" />Contacted {fmtRelTime(s.contacted_at)}
              </Badge>
            )}
          </div>
          <div className="text-xs text-slate-300 space-y-0.5">
            {s.name && <div className="flex items-center gap-1.5"><Users className="h-3 w-3" />{s.name}</div>}
            {s.email && <div className="flex items-center gap-1.5"><Mail className="h-3 w-3" /><a href={`mailto:${s.email}`} className="hover:text-emerald-500">{s.email}</a></div>}
            {s.metadata?.magnet_slug && <div className="flex items-center gap-1.5"><Building2 className="h-3 w-3" />{s.metadata.magnet_slug}{s.metadata.role ? ` · ${s.metadata.role}` : ''}</div>}
            <div className="flex items-center gap-1.5 text-[11px] pt-1"><Activity className="h-3 w-3" />{fmtRelTime(s.happened_at)} · {new Date(s.happened_at).toLocaleString()}</div>
          </div>
          {s.reply_template && !s.contacted && (
            <details className="mt-3 group">
              <summary className="cursor-pointer text-xs font-medium text-slate-300 hover:text-foreground select-none">
                <span className="group-open:hidden">📋 Show {s.lane_label} reply template</span>
                <span className="hidden group-open:inline">▼ {s.lane_label} reply template</span>
              </summary>
              <div className="mt-2 p-3 rounded-md bg-muted/40 border border-border text-xs leading-relaxed whitespace-pre-wrap" data-testid={`reply-template-${s.signal_id}`}>
                {s.reply_template}
              </div>
              <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => copyReplyTemplate(s)} data-testid={`copy-reply-${s.signal_id}`}>
                <Copy className="h-3 w-3 mr-1.5" />Copy template
              </Button>
            </details>
          )}
        </div>
        <div className="shrink-0 flex flex-col gap-2">
          <Button
            size="sm"
            variant={s.contacted ? 'outline' : 'default'}
            onClick={() => toggleContacted(s)}
            disabled={pendingId === s.signal_id}
            data-testid={`mark-${s.contacted ? 'uncontacted' : 'contacted'}-${s.signal_id}`}
            className={s.contacted ? '' : 'bg-emerald-500 hover:bg-emerald-400 text-white'}
          >
            {pendingId === s.signal_id
              ? '…'
              : s.contacted
                ? 'Re-open'
                : <><Send className="h-3 w-3 mr-1" />Mark contacted</>}
          </Button>
          {s.email && !s.contacted && (
            <a href={`mailto:${s.email}?subject=Following up · CARTIEAI&body=${encodeURIComponent(s.reply_template || '')}`}>
              <Button size="sm" variant="outline" className="w-full" data-testid={`email-${s.signal_id}`}>
                <Mail className="h-3 w-3 mr-1" />Reply
              </Button>
            </a>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="p-6 sm:p-8 max-w-5xl mx-auto space-y-6" data-testid="sales-signals-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Flame className="h-7 w-7 text-rose-500" />
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight"><BrandTitle>Sales Signals</BrandTitle></h1>
          </div>
          <p className="text-slate-300 max-w-2xl">Tiny CRM with three buyer lanes. Every signal is auto-segmented as <strong>CFO</strong> (vendor evaluation, P&amp;L proof), <strong>Engineer</strong> (PR-Cost demo, GitHub Actions), or <strong>InfoSec</strong> (subprocessors, security review) — each with a tailored founder reply you can copy in one click.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant={onlyUncontacted ? 'default' : 'outline'}
            onClick={() => setOnlyUncontacted(!onlyUncontacted)}
            data-testid="toggle-uncontacted-filter"
            className={onlyUncontacted ? 'bg-rose-500 hover:bg-rose-400 text-white' : ''}
          >
            <Filter className="h-4 w-4 mr-2" />{onlyUncontacted ? 'Showing unread' : 'Show all'}
          </Button>
          <Button onClick={load} disabled={loading} variant="outline" data-testid="refresh-btn">
            <RefreshCcw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Refresh
          </Button>
        </div>
      </div>

      {/* Lane tabs */}
      {lanes.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="lane-tabs">
          <Button
            variant={activeLane === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveLane('all')}
            data-testid="lane-tab-all"
            className={activeLane === 'all' ? 'bg-foreground text-background hover:bg-foreground/90' : ''}
          >
            All <span className="ml-1.5 opacity-60">{stats?.total || 0}</span>
          </Button>
          {lanes.map((ln) => (
            <Button
              key={ln.key}
              variant={activeLane === ln.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveLane(ln.key)}
              data-testid={`lane-tab-${ln.key}`}
              className={activeLane === ln.key ? `${laneAccent(ln.key).split(' ')[1]} text-foreground` : ''}
              title={ln.blurb}
            >
              <span className="mr-1.5">{ln.emoji}</span>
              {ln.label}
              <span className="ml-1.5 opacity-60">{ln.count}</span>
              {ln.uncontacted > 0 && (
                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-[11px] rounded-full bg-rose-500 text-white font-bold">
                  {ln.uncontacted}
                </span>
              )}
            </Button>
          ))}
        </div>
      )}

      {/* Tier tabs (priority queue by $$$) */}
      {tiers.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="tier-tabs">
          <span className="text-[11px] uppercase tracking-widest text-slate-300 font-semibold mr-1">Tier:</span>
          <Button
            variant={activeTier === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setActiveTier('all')}
            data-testid="tier-tab-all"
            className={activeTier === 'all' ? 'bg-foreground text-background hover:bg-foreground/90' : ''}
          >
            All tiers
          </Button>
          {tiers.map((t) => (
            <Button
              key={t.key}
              variant={activeTier === t.key ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTier(t.key)}
              disabled={t.count === 0}
              data-testid={`tier-tab-${t.key}`}
              className={activeTier === t.key ? 'bg-emerald-500 text-white hover:bg-emerald-400' : ''}
              title={t.blurb}
            >
              <span className="mr-1.5">{t.emoji}</span>
              {t.label}
              <span className="ml-1.5 opacity-60">{t.count}</span>
              {t.total_monthly_spend_usd > 0 && (
                <span className="ml-1.5 text-[11px] opacity-60 font-mono">
                  ${Math.round(t.total_monthly_spend_usd / 1000)}K
                </span>
              )}
            </Button>
          ))}
        </div>
      )}

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="stats-grid">
          <Card data-testid="stat-total"><CardContent className="pt-5"><div className="text-3xl font-bold">{stats.total}</div><div className="text-xs text-slate-300 uppercase tracking-wide mt-1">Total signals</div></CardContent></Card>
          <Card className="border-rose-500/30" data-testid="stat-high-intent"><CardContent className="pt-5"><div className="text-3xl font-bold text-rose-500">{stats.high_intent_uncontacted}</div><div className="text-xs text-slate-300 uppercase tracking-wide mt-1">High intent · unread</div></CardContent></Card>
          <Card data-testid="stat-uncontacted"><CardContent className="pt-5"><div className="text-3xl font-bold text-amber-500">{stats.uncontacted}</div><div className="text-xs text-slate-300 uppercase tracking-wide mt-1">Unread</div></CardContent></Card>
          <Card data-testid="stat-week"><CardContent className="pt-5"><div className="text-3xl font-bold text-emerald-400">{stats.last_7d}</div><div className="text-xs text-slate-300 uppercase tracking-wide mt-1">Last 7 days</div></CardContent></Card>
        </div>
      )}

      {loading ? (
        <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : signals.length === 0 ? (
        <Card data-testid="empty-state">
          <CardContent className="py-16 text-center text-slate-300">
            <Inbox className="h-12 w-12 mx-auto opacity-30 mb-3" />
            <p className="font-semibold">{onlyUncontacted ? 'Inbox zero! 🎉' : 'No signals yet.'}</p>
            <p className="text-sm mt-1">{onlyUncontacted ? "You've followed up on every lead." : 'Once a prospect downloads your DD pack or grabs a lead magnet, it shows up here.'}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {grouped.today.length > 0 && (
            <div data-testid="today-section">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-3">Last 24 hours · {grouped.today.length}</h2>
              <div className="space-y-2">{grouped.today.map((s) => <SignalRow key={s.signal_id} s={s} />)}</div>
            </div>
          )}
          {grouped.earlier.length > 0 && (
            <div data-testid="earlier-section">
              <h2 className="text-sm font-semibold uppercase tracking-wider text-slate-300 mb-3 mt-6">Earlier · {grouped.earlier.length}</h2>
              <div className="space-y-2">{grouped.earlier.map((s) => <SignalRow key={s.signal_id} s={s} />)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default AdminSalesSignals;
